import React, {useEffect, useState} from "react";
import Select from "react-select";
import {FaTools,} from "react-icons/fa";
import {addJobCardForWorkshopId, getInventoryForWorkshop, getWorkTypeByWorkshopId,} from "../../Api/Api";
import {useWorkshop} from "../Common/WorkshopContext";
import {useNotification} from "../Notification/NotificationContext";

const CreateJobCardModal = ({ show, handleClose, customerId }) => {
    const [formData, setFormData] = useState({
        customerId,
        vehicleNumber: "",
        workTypes: [],
        inventoryItem: [],
        estimatedTime: "",
    });

    const [workTypesOptions, setWorkTypesOptions] = useState([]);
    const [inventoryOptions, setInventoryOptions] = useState([]);
    const [selectedInventory, setSelectedInventory] = useState([]);
    const [inventoryDetails, setInventoryDetails] = useState({});
    const [loading, setLoading] = useState(false);
    const { workshopId } = useWorkshop();

    const [selectedWorkTypes, setSelectedWorkTypes] = useState([]);
    const [workTypeAmounts, setWorkTypeAmounts] = useState({});

    const { showNotification } = useNotification();

    const handleWorkTypeAmountChange = (id, amount) => {
        setWorkTypeAmounts((prev) => ({ ...prev, [id]: amount }));
    }

    useEffect(() => {
        if (show) {
            setFormData({
                customerId,
                vehicleNumber: "",
                workTypes: [],
                inventoryItem: [],
                estimatedTime: "",
            });
            setSelectedWorkTypes([]);
            setWorkTypeAmounts({});
            setSelectedInventory([]);
            setInventoryDetails({});

            fetchWorkTypes();
            fetchInventory();
        }
    }, [show, customerId]);

    const fetchWorkTypes = async () => {
        try {
            const response = await getWorkTypeByWorkshopId(workshopId);
            const options = response.data.map((item) => ({
                label: `${item.name} (₹${item.price})`,
                value: item.id,
            }));
            setWorkTypesOptions(options);
        } catch (error) {
            console.error("Error fetching work types:", error);
        }
    };

    const fetchInventory = async () => {
        try {
            const response = await getInventoryForWorkshop(workshopId,1000,0);
            const options = response.data.content.map((item) => ({
                label: `${item.name} (${item.quantity} Pcs)`,
                value: item.id,
                warrantyPeriod: item.warrantyPeriod,
            }));
            setInventoryOptions(options);
        } catch (error) {
            console.error("Error fetching inventory:", error);
        }
    };


    const handleQuantityChange = (id, quantity) => {
        setInventoryDetails((prev) => ({
            ...prev,
            [id]: { ...prev[id], quantity: parseInt(quantity, 10) || 1 },
        }));
    };

    const handleWarrantyToggle = (id, checked) => {
        setInventoryDetails((prev) => ({
            ...prev,
            [id]: { ...prev[id], underWarranty: checked },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const inventoryItem = selectedInventory?.map((item) => ({
            id: item.value,
            quantity: inventoryDetails[item.value]?.quantity || 1,
            underWarranty: inventoryDetails[item.value]?.underWarranty || false,
        }));

        const payload = {
            ...formData,
            customerId,
            workTypes: selectedWorkTypes?.map((w) => w.value),
            inventoryItem,
        };

        try {
            await addJobCardForWorkshopId(workshopId, payload);
            showNotification("Job Card Created Successfully", "success");
            handleClose();
        } catch (error) {
            console.error("Error creating job card:", error);
            showNotification(error.response.data.message, "error")
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${
                show ? "" : "hidden"
            }`}
            onClick={handleClose}
        >
            <div
                className="bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 p-6 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center border-b pb-4">
                    <h2 className="text-xl font-semibold text-blue-600 flex items-center">
                        <FaTools className="mr-2" />
                        Create Job Card
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-4 space-y-6">
                    {/* Customer and Vehicle Details */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Customer ID
                        </label>
                        <input
                            type="text"
                            value={customerId}
                            readOnly
                            className="w-full p-2 border rounded-md bg-gray-100"
                        />

                        <label className="block text-sm font-medium mt-4 text-gray-700">
                            Vehicle Number
                        </label>
                        <input
                            type="text"
                            name="vehicleNumber"
                            value={formData.vehicleNumber}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    vehicleNumber: e.target.value,
                                }))
                            }
                            required
                            className="w-full p-2 border rounded-md"
                        />
                    </div>

                    {/* Work Types */}
                    {/* Work Types Section */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Work Types
                        </label>
                        <Select
                            isMulti
                            options={workTypesOptions}
                            value={selectedWorkTypes}
                            onChange={(selected) => {
                                setSelectedWorkTypes(selected || []);
                                setWorkTypeAmounts((prev) => {
                                    const updated = {};
                                    (selected || []).forEach((item) => {
                                        updated[item.value] = prev[item.value] || { quantity: 1 };
                                    });
                                    return updated;
                                });
                            }}
                            placeholder="Search and select work types..."
                            className="react-select-container mb-2"
                            classNamePrefix="react-select"
                        />

                        {selectedWorkTypes.length === 0 && (
                            <p className="text-sm text-gray-500 mt-2">No work types selected yet.</p>
                        )}

                        <div className="space-y-2">
                            {selectedWorkTypes.map((item) => (
                                <div
                                    key={item.value}
                                    className="p-3 border rounded bg-gray-50 flex flex-col gap-2"
                                >
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">{item.label}</span>
                                        <button
                                            onClick={() => {
                                                setSelectedWorkTypes((prev) =>
                                                    prev.filter((i) => i.value !== item.value)
                                                );
                                                setWorkTypeAmounts((prev) => {
                                                    const updated = { ...prev };
                                                    delete updated[item.value];
                                                    return updated;
                                                });
                                            }}
                                            className="text-red-500 hover:text-red-700 text-sm"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    <input
                                        type="number"
                                        min="1"
                                        value={workTypeAmounts[item.value]?.quantity || ""}
                                        onChange={(e) =>
                                            handleWorkTypeAmountChange(item.value, e.target.value)
                                        }
                                        className="p-2 border rounded w-48"
                                        placeholder="Amount"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>


                    {/* Inventory Items */}{/* Inventory Items Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Inventory Items
                        </label>
                        <Select
                            isMulti
                            options={inventoryOptions}
                            value={selectedInventory} // ✅ controlled input
                            onChange={(selected) => {
                                setSelectedInventory(selected || []);

                                // Maintain quantities and warranty for only selected items
                                setInventoryDetails((prev) => {
                                    const updated = {};
                                    (selected || []).forEach((item) => {
                                        updated[item.value] = prev[item.value] || {
                                            quantity: 1,
                                            underWarranty: false,
                                        };
                                    });
                                    return updated;
                                });
                            }}
                            placeholder="Search and select inventory items..."
                            className="react-select-container mb-2"
                            classNamePrefix="react-select"
                        />

                        {/* Show selected inventory below */}
                        {selectedInventory.length === 0 && (
                            <p className="text-sm text-gray-500 mt-2">No items selected yet.</p>
                        )}

                        <div className="space-y-2">
                            {selectedInventory.map((item) => (
                                <div
                                    key={item.value}
                                    className="p-3 border rounded bg-gray-50 flex flex-col gap-2"
                                >
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">{item.label}</span>
                                        <button
                                            onClick={() => {
                                                setSelectedInventory((prev) =>
                                                    prev.filter((i) => i.value !== item.value)
                                                );
                                                setInventoryDetails((prev) => {
                                                    const updated = { ...prev };
                                                    delete updated[item.value];
                                                    return updated;
                                                });
                                            }}
                                            className="text-red-500 hover:text-red-700 text-sm"
                                        >
                                            Remove
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <input
                                            type="number"
                                            min="1"
                                            value={inventoryDetails[item.value]?.quantity || ""}
                                            onChange={(e) =>
                                                handleQuantityChange(item.value, e.target.value)
                                            }
                                            className="p-2 border rounded w-24"
                                            placeholder="Qty"
                                        />
                                        {item.warrantyPeriod > 0 && (
                                            <label className="inline-flex items-center text-sm">
                                                <input
                                                    type="checkbox"
                                                    className="mr-1"
                                                    checked={
                                                        inventoryDetails[item.value]?.underWarranty || false
                                                    }
                                                    onChange={(e) =>
                                                        handleWarrantyToggle(item.value, e.target.checked)
                                                    }
                                                />
                                                Under Warranty
                                            </label>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>


                    {/* Estimated Time */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Estimated Time (in hours)
                        </label>
                        <input
                            type="number"
                            required
                            name="estimatedTime"
                            value={formData.estimatedTime}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    estimatedTime: e.target.value,
                                }))
                            }
                            className="w-full p-2 border rounded-md"
                        />
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                    >
                        {loading ? "Creating..." : "Create Job Card"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreateJobCardModal;
